/**
 * The traits that travel with a LogRocket session (M4S-5593).
 *
 * Pure, so the trait set can be asserted without mounting a layout that drags
 * in HeroUI, next-auth and the persistent assistant.
 *
 * Two rules the ticket is explicit about, both enforced here rather than at the
 * call site:
 *
 *   1. **Omit, never placeholder.** `identify()` has no sanitiser the way
 *      `tracking.event` has `convertToTrackableValue`, so an absent value would
 *      otherwise land as `''`, `0` or `undefined` and be indistinguishable from
 *      a real one. The AC forbids placeholders such as `dev-org-1`; an `orgId`
 *      of `0` from the E2E stub org is the same defect wearing a different hat.
 *
 *   2. **View-as never overwrites the home org.** View-as is internal-only, so
 *      overwriting `orgId` would attribute mFour staff sessions to client
 *      accounts — corrupting exactly the segmentation this ticket restores.
 *
 * See docs/pdlc/observability.md.
 */

import type { DeployEnvironment } from '@/lib/config/deploy-environment';

/** What LogRocket's `identify()` accepts for a trait value. */
export type TraitValue = string | number | boolean;

export interface IdentifyTraitsInput {
    user?: {
        id?: string | null;
        name?: string | null;
        givenName?: string | null;
        familyName?: string | null;
        email?: string | null;
        isInternal?: boolean | null;
        isUnaffiliated?: boolean | null;
    } | null;
    org?: {
        orgId?: number | null;
        orgName?: string | null;
        sfAccountId?: string | null;
    } | null;
    subsidiary?: {
        parentOrgId?: number | null;
    } | null;
    /** From `useAccountViewAsTarget()`; null when not masquerading. */
    viewAsTarget?: {
        orgId: number;
        sfAccountId: string;
        orgName: string;
    } | null;
    environment: DeployEnvironment;
    buildId?: string | null;
}

/**
 * A non-empty string, or undefined. Trims first: a whitespace-only Salesforce
 * id is as useless as an absent one.
 */
function text(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

/**
 * A positive org id as a string, or undefined.
 *
 * Zero is excluded deliberately — it is what the E2E stub org carries
 * (`src/app/layout.tsx`), and under the new routing those sessions are recorded
 * rather than discarded, so `0` would otherwise reach the Test project as if it
 * were a real account.
 */
function orgIdentifier(value: number | null | undefined): string | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
    return String(value);
}

/**
 * Build the trait payload for `LogRocket.identify()`.
 *
 * `orgId` is the numeric Studio/MySQL id, stringified — not the Salesforce id.
 * This repo carries a genuine collision (`orgId` means the Salesforce id on
 * `ProjectSelection`/`Survey`, but the MySQL id on `OrganizationProfile`), so
 * `sfAccountId` and `orgName` ship alongside it under unambiguous names. A
 * support engineer asking "which client is affected?" reads `orgName`; CS and
 * Sales paste `sfAccountId` into Salesforce; an engineer puts `orgId` in a
 * `WHERE` clause.
 */
export function buildIdentifyTraits(input: IdentifyTraitsInput): Record<string, TraitValue> {
    const { user, org, subsidiary, viewAsTarget, environment, buildId } = input;

    const traits: Record<string, TraitValue> = {
        // Always present: the deployment this session ran on, and the build it ran.
        environment,
    };

    const assign = (key: string, value: TraitValue | undefined) => {
        if (value !== undefined) traits[key] = value;
    };

    // Identity — unchanged from before M4S-5593.
    assign('id', text(user?.id));
    assign('name', text(user?.name));
    assign('givenName', text(user?.givenName));
    assign('familyName', text(user?.familyName));
    assign('email', text(user?.email));

    // Organisation — the ticket's headline gap.
    assign('orgId', orgIdentifier(org?.orgId));
    assign('sfAccountId', text(org?.sfAccountId));
    assign('orgName', text(org?.orgName));
    assign('parentOrgId', orgIdentifier(subsidiary?.parentOrgId));

    // Classification — what makes "exclude mFour staff" a filter rather than a guess.
    if (typeof user?.isInternal === 'boolean') traits.isInternal = user.isInternal;
    if (typeof user?.isUnaffiliated === 'boolean') traits.isUnaffiliated = user.isUnaffiliated;

    // View-as, alongside the home org and never on top of it.
    if (viewAsTarget) {
        traits.isViewAs = true;
        assign('viewAsOrgId', orgIdentifier(viewAsTarget.orgId));
        assign('viewAsSfAccountId', text(viewAsTarget.sfAccountId));
        assign('viewAsOrgName', text(viewAsTarget.orgName));
    }

    assign('buildId', text(buildId));

    return traits;
}
